<template>
  <div
    ref="element"
    class="product-card"
    :class="[
      $blockClass(blockClass, 'product-card'),
      {
        [$class('loading')]: state === 'loading',
        'product-card--sold-out': localProduct && !localProduct.available,
        'product-card--small': small,
        'product-card--tiny': tiny,
      },
    ]"
    @mouseenter="isMobile ? null : loadSecondaryImage = true"
    @mouseleave="isMobile ? null : loadSecondaryImage = false"
  >
    <div class="product-card__image-wrapper">
      <span
        class="visually-hidden"
        v-text="localProduct.product_title"
      />

      <splide
        v-if="enableSwiper"
        ref="splide"
        :key="localProduct.handle"
        class="product-card__images"
        :has-track="false"
        :options="config"
        @splide:move="handleMove"
        @splide:hidden="(_, slide) => handleVisibleUpdate(slide, false)"
        @splide:visible="(_, slide) => handleVisibleUpdate(slide, true)"
      >
        <span
          class="visually-hidden"
          v-text="localProduct.product_title"
        />

        <splide-track v-if="images.length > 0">
          <splide-slide
            v-for="(image, imageIndex) of images"
            :key="`product-card-${image?.id}-${imageIndex}`"
          >
            <a
              :key="`images-${localProduct.handle}-${imageIndex}`"
              class="product-card__image-link"
              :href="localProduct.url"
              @click="handleClick"
            >
              <responsive-image
                :alt="getImageAlt(image)"
                class="product-card__responsive-image"
                :class="$blockClass(blockClass, 'product-card-image')"
                block-class="product-card"
                :image="state === 'ready' ? image : {}"
                min-max="200-650"
                ratio="3:4"
                :sizes="sizes"
                :style="{ animationDelay: animationDelay }"
                :lazyload="lazyload"
              />
            </a>
          </splide-slide>
        </splide-track>

        <splide-track v-else>
          <splide-slide :key="`product-card-${localProduct?.id}-placeholder`">
            <a
              :key="`images-${localProduct.handle}-placeholder`"
              class="product-card__image-link"
              :href="localProduct.url"
              @click="handleClick"
            >
              <responsive-image
                alt=""
                class="product-card__responsive-image"
                :class="$blockClass(blockClass, 'product-card-image')"
                block-class="product-card"
                :image="{}"
                min-max="200-650"
                ratio="3:4"
                :sizes="sizes"
                :style="{ animationDelay: animationDelay }"
                :lazyload="lazyload"
              />
            </a>
          </splide-slide>
        </splide-track>

        <ul class="product-card__images-pagination splide__pagination" />

        <button
          ref="quickView"
          class="product-card__quickview icon-button"
          type="button"
          @click.prevent="handleQuickViewClick"
        >
          <span
            class="product-card__quickview-text text-label-s"
            v-text="$string('product.card.quickview')"
          />

          <icon-cart />
        </button>
      </splide>

      <div
        v-else
        class="product-card__featured-image"
      >
        <a
          :key="`featured-image-${localProduct.handle}`"
          class="product-card__image-link"
          :href="localProduct.url"
          @click="handleClick"
        >
          <responsive-image
            :alt="getImageAlt(localProduct.featuredImage)"
            class="product-card__responsive-image"
            :class="$blockClass(blockClass, 'product-card-image')"
            block-class="product-card"
            :image="state === 'ready' ? localProduct.featuredImage : {}"
            min-max="200-650"
            ratio="3:4"
            :sizes="sizes"
            :style="{ animationDelay: animationDelay }"
            :lazyload="lazyload"
          />
        </a>

        <a
          v-if="localProduct?.images?.length > 1 && loadSecondaryImage"
          :key="`second-image-${localProduct.handle}`"
          class="product-card__image-link product-card__image-link--hover"
          :href="localProduct.url"
          @click="handleClick"
        >
          <responsive-image
            :alt="getImageAlt(localProduct.images[1])"
            class="product-card__responsive-hover-image"
            :class="$blockClass(blockClass, 'product-card-image')"
            block-class="product-card"
            :image="state === 'ready' ? localProduct.images[1] : {}"
            min-max="200-650"
            :sizes="sizes"
            :padding="false"
            :lazyload="lazyload"
          />
        </a>
      </div>

      <div
        v-if="enableSwiper"
        class="product-card__images-arrows"
      >
        <btn
          class="
            product-card__images-arrow
            product-card__images-arrow--prev
          "
          :label="$string('accessibility.carousel.previous')"
          modifiers="text,round"
          type="button"
          @click.prevent="handleArrowClick('previous')"
        >
          <template #icon>
            <icon-chevron-left />
          </template>
        </btn>

        <btn
          class="
            product-card__images-arrow
            product-card__images-arrow--next
          "
          :label="$string('accessibility.carousel.next')"
          modifiers="text,round"
          type="button"
          @click.prevent="handleArrowClick('next')"
        >
          <template #icon>
            <icon-chevron-right />
          </template>
        </btn>
      </div>

      <product-badges
        v-if="localProduct"
        :key="`badges-${localProduct.handle}`"
        class="product-card__badges"
        :badges="product?.badges"
        :product="localProduct"
        :tiny="tiny"
      />

      <wishlist-button
        v-if="localProduct?.variants?.[0] && localProduct?.handle"
        class="product-card__wishlist-button"
        :product="localProduct"
        template="card"
        :variant="localProduct.variants?.[0]"
      />

      <button
        v-if="!enableSwiper"
        ref="quickView"
        class="product-card__quickview icon-button"
        type="button"
        @click="handleQuickViewClick"
      >
        <span
          class="product-card__quickview-text text-label-s"
          v-text="$string('product.card.quickview')"
        />

        <icon-cart />
      </button>
    </div>

    <div
      class="product-card__footer"
      :class="$blockClass(blockClass, 'product-card-footer')"
    >
      <p
        v-if="localProduct.vendor || state === 'loading'"
        class="product-card__vendor text-heading-2xs"
        :class="[
          $blockClass(blockClass, 'product-card-vendor'),
          { 'text-heading-xs-desktop': !tiny },
        ]"
        :style="{ animationDelay: animationDelay }"
        v-text="localProduct.vendor"
      />

      <a
        class="product-card__title text-body-xs"
        :class="[
          $blockClass(blockClass, 'product-card-title'),
          { 'text-body-s-desktop': !tiny },
        ]"
        :href="localProduct.url"
        :style="{ animationDelay: animationDelay }"
        @click="handleClick"
        v-text="localProduct.product_title"
      />

      <div class="product-card__meta">
        <product-prices
          :class="$blockClass(blockClass, 'product-card-prices')"
          :class-normal="`text-heading-2xs ${tiny ? '' : 'text-heading-xs-desktop'}`"
          :class-sold-out="`text-body-xs ${tiny ? '' : 'text-body-s-desktop'}`"
          :available="localProduct?.available"
          block-class="product-card"
          :compare-at-price="localProduct?.compareAtPrice?.amount"
          :is-coming-soon="localProduct?.isComingSoon"
          :loading="localProduct?.price?.loading"
          :price="localProduct?.price?.amount"
          :style="{ animationDelay: animationDelay }"
          :varies="localProduct?.price?.varies"
        />

        <colour-swatches
          v-if="localColours && Object.keys(localColours).length > 1 && !tiny"
          class="product-card__swatches"
          :animation-delay="animationDelay"
          :colours="localColours"
          :handle="localProduct.handle"
          limited
          :state="state"
          :visible="visible"
          @update-handle="handleUpdateHandle"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product card
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Boolean} [delayClick] - Delay click event to save/submit data.
 * @param {Number} [index] - Product card's forloop index.
 * @param {Boolean|String} [nostoResultId] - Nosto result Id for tracking.
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {Boolean} [small] - Move colour swatches below footer on mobile.
 * @param {Boolean} [swiperCarousel] - Enable/disable Swiper carousel.
 * @param {Boolean} [tiny] - Hide colour swatches and use smaller font sizes.
 * @param {Boolean} [visible] - Product card is visible in carousel.
 * @param {Boolean|String} [lazyload] - Lazy loading for product images
 *
 */
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import { mapActions, mapState } from 'vuex'
import { nextTick } from 'vue'

import Btn from '~global/btn/btn'
import ColourSwatches from '~async/colour-swatches/colour-swatches'
import ProductBadges from '~async/product-badges/product-badges'
import ProductPrices from '~global/product-prices/product-prices'
import WishlistButton from '~async/wishlist-button/wishlist-button'
import ResponsiveImage from '~global/images/responsive-image'

import IconCart from '~icons/misc/cart.svg'
import IconChevronLeft from '~icons/direction/chevron/left.svg'
import IconChevronRight from '~icons/direction/chevron/right.svg'

import breakpoints from '~/config/breakpoints'
import query from '~/graphql/queries/product.gql'
import { formatProduct } from '~/helpers/format'
import { setIntersectionObserver } from '~/helpers/general'

export default {
  name: 'ProductCard',

  components: {
    Btn,
    ColourSwatches,
    IconCart,
    IconChevronLeft,
    IconChevronRight,
    ProductBadges,
    ProductPrices,
    WishlistButton,
    ResponsiveImage,
    Splide,
    SplideSlide,
    SplideTrack,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    handle: {
      type: String,
      default: '',
    },
    delayClick: {
      type: Boolean,
      default: false,
    },
    index: {
      type: Number,
      default: 0,
    },
    nostoResultId: {
      type: [Boolean, String],
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    small: {
      type: Boolean,
      default: false,
    },
    swiperCarousel: {
      type: Boolean,
      default: true,
    },
    tiny: {
      type: Boolean,
      default: false,
    },
    visible: {
      type: Boolean,
      default: true,
    },
    lazyload: {
      type: [Boolean, String],
      default: 'lazy',
    },
  },

  emits: {
    'view-product': (payload) => typeof payload === 'string',
  },

  data() {
    return {
      config: {
        arrows: false,
        direction: cnvs.store.language === 'ar' ? 'rtl' : 'ltr',
        drag: true,
        flickPower: 100,
        flickMaxPages: 1,
        keyboard: true,
        mediaQuery: 'min',
        pagination: true,
        speed: this.$timing('slower'),
        type: 'loop',
      },
      displayed: false,
      loadSecondaryImage: false,
      localColours: false,
      localProduct: false,
      slides: {
        current: 0,
        last: 0,
      },
      state: 'ready',
      visibleSlides: [],
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute animation delay.
     * @returns {String}
     */
    animationDelay() {
      return this.$timing('quick', { index: this.index, unit: 's' })
    },

    /**
     * Compute if quickview is open.
     * @returns {Boolean}
     */
    quickviewIsOpen() {
      return this.quickview !== ''
    },

    /**
     * Compute enable swiper.
     * @returns {Boolean}
     */
    enableSwiper() {
      if (!this.swiperCarousel || !this.displayed) {
        return false
      }

      if (this.isMobile) {
        return cnvs.settings.enableProductCardSwipeMobile
      }

      return cnvs.settings.enableProductCardSwipeDesktop
    },

    /**
     * Compute swiper range.
     * @returns {Number}
     */
    swiperRange() {
      if (this.isMobile) {
        return cnvs.settings.productCardSwipeMobile
      }

      return cnvs.settings.productCardSwipeDesktop
    },

    /**
     * Compute swiper images.
     * @returns {Array}
     */
    images() {
      if (!this.localProduct || !this.enableSwiper) {
        return []
      }

      return this.localProduct.images.slice(0, this.swiperRange)
    },

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return `(min-width: ${breakpoints.l}) 25vw, 100vw`
    },
  },

  watch: {

    /**
     * Watch product updates to update local product.
     * - Update state when false.
     */
    product: {
      handler() {
        nextTick().then(() => {
          if (!this.product && this.handle) {
            this.queryProduct(this.handle)
            return
          }

          this.setLocalProduct()
          this.state = this.localProduct === false ? 'loading' : 'ready'
        })
      },
      immediate: true,
    },

    /**
     * Watch images update carousel.
     */
    images() {

      /**
       * Update slide pagination data.
       */
      this.slides = {
        current: 0,
        last: this.images.length - 1,
      }

      /**
       * Update visible slides data.
       */
      this.visibleSlides = [...Array(this.images.length)].map((_, index) => {
        return index < 1
      })
    },
  },

  mounted() {
    setIntersectionObserver({
      callback: this.handleIntersection,
      element: this.$refs.element,
      threshold: [0, 1],
    })

    if (!cnvs.storybook || this.localProduct) {
      return
    }

    if (!this.product) {
      this.state = 'loading'
      return
    }

    this.setLocalProduct()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setQuickViewHandle: 'quick-view/setHandle',
    }),

    /**
     * Set local product.
     */
    setLocalProduct() {
      if (
        this.localProduct.handle === this.product.handle &&
        this.localProduct.price?.loading === this.product.price?.loading
      ) {
        return
      }

      this.localProduct = this.product
      this.localColours = this.product.colours
      this.setStorage(this.localProduct)
    },

    /**
     * Return image alt text. Default to product title if alt text was not
     * set in Shopify.
     * @param {Object} image - image object.
     * @returns {String}
     */
    getImageAlt(image) {
      return this.state === 'ready' && image?.alt
        ? image.alt
        : this.localProduct.product_title
    },

    /**
     * Update display product based on handle emit.
     * @param {String} handle - New handle to load.
     */
    handleUpdateHandle(handle) {
      if (handle === false || handle === this.localProduct.handle) {
        return
      }

      if (
        !cnvs.settings.disableProductSaving &&
        sessionStorage.getItem(`cnvs-product-${handle}`)
      ) {
        this.localProduct = JSON.parse(sessionStorage.getItem(`cnvs-product-${handle}`))
        return
      }

      this.queryProduct(handle)
    },

    /**
     * Query product.
     * @param {String} handle - Handle to load.
     */
    async queryProduct(handle) {
      this.state = 'loading'

      try {
        const response = await cnvs.Query({
          query,
          variables: {
            country: cnvs.store.country,
            handle,
            language: cnvs.store.language,
          },
        })

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        this.localProduct = formatProduct(response)
        this.setStorage(this.localProduct)
        this.state = 'ready'

      } catch (error) {
        reportError('Failed to load product', error)
      }
    },

    /**
     * Set product to sessionStorage.
     * @param {Object} product - Product to update.
     */
    setStorage(product) {
      if (cnvs.settings.disableProductSaving) {
        return
      }

      sessionStorage.setItem(
        `cnvs-product-${product.handle}`,
        JSON.stringify(product),
      )
    },

    /**
     * Handle product card click.
     * - Prevents click so collection data can be saved first or Boost analytics
     *  data submitted on search.
     * @param {Object} event - Click event.
     */
    handleClick(event) {
      if (!this.delayClick) {
        return
      }

      event.preventDefault()
      this.$emit('view-product', this.localProduct.url)
    },

    /**
     * Handle quick view button click.
     * - Opens the quick view modal.
     * - Removes any existing colour order data from session storage.
     */
    handleQuickViewClick() {
      const data = {
        handle: this.localProduct.handle,
        resultId: this.nostoResultId,
      }
      this.setQuickViewHandle(data)
      sessionStorage.removeItem('cnvs-quickview-colour-order')
      this.$refs.quickView.blur()
    },

    /**
     * Handle arrow click.
     * - Using custom function because if we use 'rewind' then it skips any
     *   incomplete pages, if we use 'loop' then it fills empty spaces with
     *   improperly initialised product card components.
     * @param {String} type - Type of arrow, `next` or `previous`.
     */
    handleArrowClick(type) {
      if (type === 'next') {
        if (this.slides.current >= this.slides.last) {
          this.$refs.splide.go(0)
          return
        }

        this.$refs.splide.go('>')
        return
      }

      if (this.slides.current === 0) {
        this.$refs.splide.go(this.slides.last)
        return
      }

      this.$refs.splide.go('<')
    },

    /**
     * Handle element intersection.
     * - Initialize the swiper after the element displayed.
     * @param {Array} entries
     */
    handleIntersection(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.displayed = true
        }
      })
    },

    /**
     * Handle carousel move event.
     * @param {Number} newIndex - The new visible index.
     */
    handleMove(_, newIndex) {
      this.slides.current = newIndex
    },

    /**
     * Handle visible.
     */
    handleVisibleUpdate(slide, visible) {
      this.visibleSlides[slide.index] = visible
    },
  },
}
</script>

<style lang="scss">
@import './product-card';
</style>
